import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { ensureGuid } from '../utils/id';
import { validateTransaction, hasErrors } from '../utils/validation';

const STATUS_OPTIONS = ['Pending', 'Completed', 'Failed', 'Cancelled'];
const TRANSACTION_TYPES = ['Sale', 'Purchase'];

const createInitialForm = () => ({
  id: '',
  createdDatetime: '',
  itemName: '',
  customerVendorName: '',
  transactionType: 'Sale',
  quantity: '1',
  total: '1',
  status: 'Completed',
  notes: '',
});

const buildTransactionsPath = ({ ym, type, counterparty }) => {
  const params = new URLSearchParams();
  if (ym && ym.length === 7) params.append('ym', ym);
  if (type) params.append('transactionType', type);
  if (counterparty) params.append('customerVendorName', counterparty);
  const query = params.toString();
  return query ? `transactions?${query}` : 'transactions';
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const toIsoMidnight = (dateOnly) => {
  if (!dateOnly) return '';
  const parsed = new Date(`${dateOnly}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString();
};

const normalizeType = (value) => (String(value).toLowerCase() === 'purchase' || value === 1 ? 'Purchase' : 'Sale');

const formatDisplayDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const buildTransactionPayload = (form) => {
  const payload = {
    id: ensureGuid(form.id),
    itemName: form.itemName,
    customerVendorName: form.customerVendorName,
    transactionType: form.transactionType,
    quantity: Number(form.quantity),
    total: Number(form.total),
    status: form.status,
    notes: form.notes,
    tableId: 'Transaction',
  };
  const created = toIsoMidnight(form.createdDatetime);
  if (created) {
    payload.createdDatetime = created;
    payload.YM = `${created.slice(0, 4)}-${created.slice(5, 7)}`;
  }
  return payload;
};

function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [filters, setFilters] = useState({ ym: '', type: '', counterparty: '' });
  const [form, setForm] = useState(() => createInitialForm());
  const [itemsLookup, setItemsLookup] = useState([]);
  const [customersLookup, setCustomersLookup] = useState([]);
  const [vendorsLookup, setVendorsLookup] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [attempted, setAttempted] = useState(false);
  const requestIdRef = useRef(0);

  const errors = validateTransaction(form);
  const isValid = !hasErrors(errors);

  const loadLookups = useCallback(async () => {
    setLookupsLoading(true);
    try {
      const [items, customers, vendors] = await Promise.all([
        api.list('items'),
        api.list('customers'),
        api.list('vendors'),
      ]);
      setItemsLookup(Array.isArray(items) ? items : []);
      setCustomersLookup(Array.isArray(customers) ? customers : []);
      setVendorsLookup(Array.isArray(vendors) ? vendors : []);
    } catch (err) {
      console.warn('related lists load failed', err);
    } finally {
      setLookupsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  const loadTransactions = useCallback(async () => {
    const currentRequest = ++requestIdRef.current;
    setLoadingList(true);
    setError(null);
    try {
      const path = buildTransactionsPath(filters);
      const data = await api.list(path);
      if (requestIdRef.current === currentRequest) {
        setTransactions(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      if (requestIdRef.current === currentRequest) {
        setTransactions([]);
        setError(err.message || String(err));
      }
    } finally {
      if (requestIdRef.current === currentRequest) {
        setLoadingList(false);
      }
    }
  }, [filters]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const availableCounterparties = form.transactionType === 'Sale' ? customersLookup : vendorsLookup;

  const counterpartyFilterOptions = useMemo(() => {
    if (filters.type === 'Sale') {
      return customersLookup;
    }
    if (filters.type === 'Purchase') {
      return vendorsLookup;
    }
    const seen = new Set();
    return [...customersLookup, ...vendorsLookup].filter((entry) => {
      const key = entry && entry.name;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [filters.type, customersLookup, vendorsLookup]);

  const handleFormChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'transactionType') {
        next.customerVendorName = '';
      }
      return next;
    });
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm(createInitialForm());
    setAttempted(false);
    setMessage('');
    setError(null);
  };

  const save = async () => {
    if (!isValid) {
      setAttempted(true);
      return;
    }
    setSaving(true);
    setError(null);
    setMessage('');
    try {
      const payload = buildTransactionPayload(form);
      if (form.id) {
        await api.update('transactions', form.id, payload);
      } else {
        await api.create('transactions', payload);
      }
      setMessage('保存成功');
      setForm(createInitialForm());
      setAttempted(false);
      await loadTransactions();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const edit = (entry) => {
    setForm({
      id: entry.id || '',
      createdDatetime: toDateInputValue(entry.createdDatetime || entry.timestamp || entry.date || ''),
      itemName: entry.itemName || '',
      customerVendorName: entry.customerVendorName || '',
      transactionType: normalizeType(entry.transactionType),
      quantity: entry.quantity !== undefined ? String(entry.quantity) : '',
      total: entry.total !== undefined ? String(entry.total) : '',
      status: entry.status || 'Completed',
      notes: entry.notes || '',
    });
    setAttempted(false);
    setMessage('');
  };

  const removeTransaction = async (id) => {
    try {
      await api.remove('transactions', id);
      await loadTransactions();
    } catch (err) {
      console.error(err);
      setError(err.message || String(err));
    }
  };

  const confirmRemove = (id) => {
    if (window.confirm('Delete this transaction?')) {
      removeTransaction(id);
    }
  };

  const clearFilters = () => {
    setFilters({ ym: '', type: '', counterparty: '' });
  };

  const renderSelectOptions = (items) =>
    items
      .filter((entry) => entry && entry.name)
      .map((entry) => (
        <option key={entry.id || entry.name} value={entry.name}>
          {entry.name}
        </option>
      ));

  return (
    <div className="page transactions-responsive">
      <aside className="card transactions-form">
        <h3>交易</h3>
        {error && <div className="alert error">{error}</div>}
        {message && <div className="alert info">{message}</div>}
        <div className="form-row">
          <label>日期</label>
          <input type="date" value={form.createdDatetime} onChange={(e) => handleFormChange('createdDatetime', e.target.value)} />
        </div>
        <div className="form-row">
          <label>商品</label>
          <select
            className={attempted && errors.itemName ? 'invalid' : ''}
            value={form.itemName}
            onChange={(e) => handleFormChange('itemName', e.target.value)}
            disabled={!itemsLookup.length && lookupsLoading}
          >
            <option value="">-- select item --</option>
            {renderSelectOptions(itemsLookup)}
          </select>
          {attempted && errors.itemName && <div className="field-error">{errors.itemName}</div>}
        </div>
        <div className="form-row">
          <label>交易类型</label>
          <select
            className={attempted && errors.transactionType ? 'invalid' : ''}
            value={form.transactionType}
            onChange={(e) => handleFormChange('transactionType', e.target.value)}
          >
            {TRANSACTION_TYPES.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          {attempted && errors.transactionType && <div className="field-error">{errors.transactionType}</div>}
        </div>
        <div className="form-row">
          <label>数量</label>
          <input
            className={attempted && errors.quantity ? 'invalid' : ''}
            type="number"
            step="any"
            value={form.quantity}
            onChange={(e) => handleFormChange('quantity', e.target.value)}
          />
          {attempted && errors.quantity && <div className="field-error">{errors.quantity}</div>}
        </div>
        <div className="form-row">
          <label>合计</label>
          <input
            className={attempted && errors.total ? 'invalid' : ''}
            type="number"
            value={form.total}
            onChange={(e) => handleFormChange('total', e.target.value)}
          />
          {attempted && errors.total && <div className="field-error">{errors.total}</div>}
        </div>
        <div className="form-row">
          <label>往来方</label>
          <select
            className={attempted && errors.customerVendorName ? 'invalid' : ''}
            value={form.customerVendorName}
            onChange={(e) => handleFormChange('customerVendorName', e.target.value)}
            disabled={!availableCounterparties.length && lookupsLoading}
          >
            <option value="">-- select counterparty --</option>
            {renderSelectOptions(availableCounterparties)}
          </select>
          {attempted && errors.customerVendorName && <div className="field-error">{errors.customerVendorName}</div>}
        </div>
        <div className="form-row">
          <label>状态</label>
          <select value={form.status} onChange={(e) => handleFormChange('status', e.target.value)}>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <label>备注</label>
          <textarea value={form.notes} onChange={(e) => handleFormChange('notes', e.target.value)} />
        </div>
        <div className="form-actions">
          <button className="btn" onClick={save} disabled={saving || loadingList}>
            {saving ? '保存中...' : '保存'}
          </button>
          <button className="btn secondary" onClick={resetForm}>
            重置
          </button>
        </div>
      </aside>
      <div className="transactions-list-area">
        <div className="card transactions-filters">
          <label>按年月：</label>
          <input type="month" value={filters.ym} onChange={(e) => handleFilterChange('ym', e.target.value)} />
          <label>交易类型：</label>
          <select value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)}>
            <option value="">全部</option>
            {TRANSACTION_TYPES.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <label>往来方：</label>
          <select value={filters.counterparty} onChange={(e) => handleFilterChange('counterparty', e.target.value)}>
            <option value="">全部</option>
            {renderSelectOptions(counterpartyFilterOptions)}
          </select>
          <button className="btn secondary" onClick={clearFilters}>清除</button>
        </div>
        <section className="card list">
          <h3>交易列表</h3>
          {loadingList ? (
            <div>Loading...</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>日期</th>
                  <th>商品</th>
                  <th>数量</th>
                  <th>合计</th>
                  <th>往来方</th>
                  <th>状态</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatDisplayDate(entry.createdDatetime || entry.timestamp || entry.date)}</td>
                    <td>{entry.itemName}</td>
                    <td>{entry.quantity}</td>
                    <td>{entry.total}</td>
                    <td>{entry.customerVendorName}</td>
                    <td>{entry.status}</td>
                    <td className="actions">
                      <button className="btn ghost" onClick={() => edit(entry)}>编辑</button>
                      <button className="btn secondary" onClick={() => confirmRemove(entry.id)}>删除</button>
                    </td>
                  </tr>
                ))}
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan="7">暂无交易</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
}

export default Transactions;
